import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { RegistrationService } from 'src/app/core/api-services/user-profile/registration.service';
import { UserProfileLookups } from 'src/app/core/models/user-profile/user-profile.model';
import { BaseComponent } from 'src/app/shared/base/BaseComponent';
import { InputPatterns } from 'src/app/shared/input-components/validators/InputPatterns';
import { ProfileMapper } from '../profile-mapper';
import {
  lessThanOrEqualDate,
  moreThanOrEqualDate,
} from 'src/app/shared/input-components/validators/more-than-date.validator';
import { UserProfileService } from 'src/app/core/api-services/user-profile/user-profile.service';
import { Observable } from 'rxjs';
import { EsResponseModel } from 'src/app/core/api-services/shared/EsResponseModel';
import { LicensesIssuerEnum } from 'src/app/core/enums/Licenses-Issuer-codes.enum';

@Component({
  selector: 'profile-company-info',
  templateUrl: 'profile-company-info.component.html',
})
export class ProfileCompanyInfoComponent
  extends BaseComponent
  implements OnInit
{
  @Input() form: FormGroup;
  @Input() lookups: UserProfileLookups;
  @Input() isRegistered: boolean = false;

  @Output() onLicenseIssuerChange = new EventEmitter<boolean>();

  constructor(
    translateService: TranslateService,
    private _registrationService: RegistrationService,
    private _userProfileService: UserProfileService,
    private _mapper: ProfileMapper
  ) {
    super(translateService);
  }

  ngOnInit(): void {
    this.form.addControl(
      'NameAr',
      new FormControl(null, [
        Validators.required,
        Validators.pattern(InputPatterns.AlphaNumericEnglishOrArabic),
      ])
    );
    this.form.addControl(
      'NameEn',
      new FormControl(null, [
        Validators.required,
        Validators.pattern(InputPatterns.AlphaNumericEnglish),
      ])
    );
    this.form.addControl('Emirate', new FormControl(null, Validators.required));
    this.form.addControl(
      'LicenseIssuer',
      new FormControl(null, Validators.required)
    );
    this.form.addControl(
      'LegalStatus',
      new FormControl(null, Validators.required)
    );
    this.form.addControl(
      'LicenseType',
      new FormControl(null, Validators.required)
    );
    this.form.addControl(
      'LicenseNumber',
      new FormControl(null, Validators.required)
    );
    this.form.addControl(
      'LicenseIssuanceDate',
      new FormControl(null, [
        Validators.required,
        lessThanOrEqualDate(new Date()),
      ])
    );
    this.form.addControl(
      'LicenseExpiryDate',
      new FormControl(null, [
        Validators.required,
        moreThanOrEqualDate(new Date()),
      ])
    );
    this.form.addControl('Owners', new FormControl(null, []));
  }

  onEmirateChange() {
    this.lookups.LicenseIssuers = [];
    this.form.controls.LicenseIssuer.reset();
    var emirateId = this.form.controls.Emirate.value?.id;
    if (emirateId) {
      if(this.isRegistered){
        this._userProfileService
        .GetLicenseIssuers(emirateId)
        .subscribe((response) => {
          if (response) {
            this.lookups.LicenseIssuers = this._mapper.mapLookup(response);
          }
        });
      }
      else{
        this._registrationService
        .GetLicenseIssuers(emirateId)
        .subscribe((response) => {
          if (response) {
            this.lookups.LicenseIssuers = this._mapper.mapLookup(response);
          }
        });
      }
      this.validateName(this.form.controls.NameAr, emirateId, 'ar');
      this.validateName(this.form.controls.NameEn, emirateId, 'en');
    }
  }

  onNameArChange() {
    if (this.form.controls.NameAr.valid) {
      var emirateId = this.form.controls.Emirate.value?.id;
      this.validateName(this.form.controls.NameAr, emirateId, 'ar');
    }
  }

  onNameEnChange() {
    if (this.form.controls.NameEn.valid) {
      var emirateId = this.form.controls.Emirate.value?.id;
      this.validateName(this.form.controls.NameEn, emirateId, 'en');
    }
  }

  onIssuerChange() {
    if (this.form.controls.LicenseIssuer.valid) {
      this.setLicenseValidation();
      this.validateLicense();
    }
  }

  onLicenseNumberChange() {
    if (this.form.controls.LicenseNumber.valid) this.validateLicense();
  }

  validateName(ctrl: any, emirateId: number, lang: string) {
    var name = ctrl.value;
    if (name && emirateId && lang) {
      this._registrationService
        .ValidateAuthorityName(name, emirateId, lang)
        .subscribe((response) => {
          if (response.IsSuccess && response.Data) {
            ctrl.setErrors(null);
          } else {
            ctrl.setErrors({ customError: true });
          }
        });
    }
  }

  setLicenseValidation() {
    var licenseIssuerCode = this.form.controls.LicenseIssuer.value?.code;
    if (
      licenseIssuerCode == LicensesIssuerEnum.GOVEntity ||
      licenseIssuerCode == LicensesIssuerEnum.OutsideUAE
    ) {
      this.form.controls.LicenseType.removeValidators([Validators.required]);
      this.form.controls.LicenseNumber.removeValidators([Validators.required]);
      this.form.controls.LicenseIssuanceDate.removeValidators([
        Validators.required,
      ]);
      this.form.controls.LicenseExpiryDate.removeValidators([
        Validators.required,
      ]);
      this.onLicenseIssuerChange.emit(false);
    } else {
      this.form.controls.LicenseType.addValidators([Validators.required]);
      this.form.controls.LicenseNumber.addValidators([Validators.required]);
      this.form.controls.LicenseIssuanceDate.addValidators([
        Validators.required,
      ]);
      this.form.controls.LicenseExpiryDate.addValidators([Validators.required]);
      this.onLicenseIssuerChange.emit(true);
    }
    this.form.controls.LicenseType.updateValueAndValidity();
    this.form.controls.LicenseNumber.updateValueAndValidity();
    this.form.controls.LicenseIssuanceDate.updateValueAndValidity();
    this.form.controls.LicenseExpiryDate.updateValueAndValidity();
  }

  validateLicense() {
    var licenseIssuerId = this.form.controls.LicenseIssuer.value?.id;
    var licenseNumber = this.form.controls.LicenseNumber.value;
    if (licenseNumber && licenseIssuerId) {
      if(this.isRegistered){
        this._userProfileService
        .ValidateLicenseNumber(licenseNumber, licenseIssuerId)
        .subscribe((response) => {
          if (response) {
            this.form.controls.LicenseNumber.setErrors(null);
          } else {
            this.form.controls.LicenseNumber.setErrors({ customError: true });
          }
        });
      } else {
        this._registrationService
        .ValidateLicenseNumber(licenseNumber, licenseIssuerId)
        .subscribe((response) => {
          if (response) {
            this.form.controls.LicenseNumber.setErrors(null);
          } else {
            this.form.controls.LicenseNumber.setErrors({ customError: true });
          }
        });
      }
    }
  }
}
